<script setup lang="ts">
/**
 * Aura AGM (MULTIPLE) — 멤버 서명 블록(**필드 방식**, 싸이너 UI 아님).
 *   문서에 바로 편집하는 서명 그리드: 멤버마다 서명선 + 이름 필드 + (법인이면) 대표자 이름 필드.
 *   저장 = fill 키(directors 패턴과 동일): agm_member_count / agm_member_{i}_name /
 *          agm_member_{i}_representative_name / agm_member_{i}_is_corporate.
 *   normalize 가 이 키들로 members 배열(member_name + member_address)을 만들어 문서 멤버 루프에 반영.
 *   Consent·Attendance 두 표가 같은 fill 키를 공유 → 한쪽 입력이 양쪽 반영.
 */
import { computed } from 'vue';
import CddVarField from '../../agm/cdd/CddVarField.vue';
import { useAgmFill } from '../../agm/cdd/useCddFill';

withDefaults(defineProps<{ boardLabel?: string }>(), { boardLabel: 'Members' });
const fill = useAgmFill();

const count = computed(() => {
  const n = parseInt(String(fill.values['agm_member_count'] ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
});
const indices = computed(() => Array.from({ length: count.value }, (_, i) => i + 1));
// 2열 그리드용 행 묶음
const rows = computed(() => {
  const out: number[][] = [];
  for (let i = 0; i < indices.value.length; i += 2) out.push(indices.value.slice(i, i + 2));
  return out;
});

function nameKey(i: number) { return `agm_member_${i}_name`; }
function repKey(i: number) { return `agm_member_${i}_representative_name`; }
function corpKey(i: number) { return `agm_member_${i}_is_corporate`; }
function isCorp(i: number) {
  const v = String(fill.values[corpKey(i)] ?? '').toLowerCase();
  return v === 'yes' || v === 'true' || v === '1';
}
function toggleCorp(i: number) { fill.setValue(corpKey(i), isCorp(i) ? 'no' : 'yes'); }

function addMember() { fill.setValue('agm_member_count', String(count.value + 1)); }
function removeMember(i: number) {
  const n = count.value;
  for (let k = i; k < n; k++) {
    fill.setValue(nameKey(k), String(fill.values[nameKey(k + 1)] ?? ''));
    fill.setValue(repKey(k), String(fill.values[repKey(k + 1)] ?? ''));
    fill.setValue(corpKey(k), String(fill.values[corpKey(k + 1)] ?? ''));
  }
  fill.setValue(nameKey(n), '');
  fill.setValue(repKey(n), '');
  fill.setValue(corpKey(n), '');
  fill.setValue('agm_member_count', String(Math.max(0, n - 1)));
}
</script>

<template>
  <div class="msg">
    <div v-if="boardLabel" class="msg-board">{{ boardLabel }}</div>

    <div v-if="!count" class="msg-empty">No members yet.</div>

    <div v-else class="msg-rows">
      <div v-for="(row, ri) in rows" :key="ri" class="msg-row">
        <div v-for="i in row" :key="i" class="msg-cell">
          <button type="button" class="msg-rm" title="Remove member" @click="removeMember(i)">
            <v-icon size="14">mdi-close</v-icon>
          </button>
          <div class="msg-line">______________________________</div>
          <!-- 대표자 있는 멤버: 한 줄 "[대표법인] (Duly Appointed Representative of [멤버])" -->
          <div v-if="isCorp(i)" class="msg-body">
            <CddVarField :field-key="repKey(i)" label="Corporate Representative Name" />
            <span class="msg-txt"> (Duly Appointed Representative of </span>
            <CddVarField :field-key="nameKey(i)" label="Member Name" />
            <span class="msg-txt">)</span>
          </div>
          <!-- 일반 멤버: 이름만 -->
          <div v-else class="msg-name"><CddVarField :field-key="nameKey(i)" label="Member Name" /></div>
          <label class="msg-corp">
            <input type="checkbox" :checked="isCorp(i)" @change="toggleCorp(i)" /> Has corporate representative
          </label>
        </div>
      </div>
    </div>

    <button type="button" class="msg-add" @click="addMember">
      <v-icon size="15">mdi-account-plus-outline</v-icon> Add member
    </button>
  </div>
</template>

<style scoped>
.msg { margin: 22px 0 6px; }
.msg-board { font-weight: 800; font-size: 12.5px; text-align: center; margin-bottom: 16px; }
.msg-empty { text-align: center; font-size: 12px; color: #a4adbb; padding: 8px 0; }
.msg-rows { display: block; }
.msg-row { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 22px; }
.msg-row:first-child { margin-top: 4px; }
.msg-cell { position: relative; min-width: 0; text-align: center; border: 1px dashed transparent; border-radius: 10px; padding: 6px 8px; }
.msg-cell:hover { border-color: #e2e8f2; }
.msg-cell:hover .msg-rm { opacity: 1; }
.msg-rm { position: absolute; top: 2px; right: 2px; opacity: 0; border: none; background: none; color: #c0c8d4; cursor: pointer; display: inline-flex; padding: 2px; transition: opacity .12s; }
.msg-rm:hover { color: #d64545; }
.msg-line { letter-spacing: 1px; color: #333; white-space: nowrap; }
.msg-name { font-size: 12.5px; font-weight: 700; margin-top: 3px; display: flex; justify-content: center; }
.msg-body { margin-top: 3px; display: flex; flex-wrap: wrap; align-items: baseline; justify-content: center; gap: 2px; font-size: 12.5px; font-weight: 700; }
.msg-txt { color: #1a2336; }
.msg-corp { display: inline-flex; align-items: center; gap: 5px; margin-top: 5px; font-size: 11px; color: #6b7a94; cursor: pointer; user-select: none; }
.msg-corp input { width: 14px; height: 14px; cursor: pointer; }
.msg-add { display: inline-flex; align-items: center; gap: 5px; margin-top: 18px; border: 1.5px dashed #b9c9e6; background: #f7faff; color: #1B64DA; font-size: 12px; font-weight: 800; border-radius: 10px; padding: 8px 14px; cursor: pointer; }
.msg-add:hover { background: #eef4ff; border-color: #8fb2ee; }
</style>
